import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onIdTokenChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { auth } from './firebase.js';

/**
 * Acceso por enlace magico.
 *
 * No hay contrasenas en este producto: el usuario pide un enlace, lo recibe
 * por correo y entra. Eso elimina de un plumazo las contrasenas reutilizadas,
 * el relleno de credenciales y todo el trabajo de recuperacion de cuenta.
 *
 * Dos detalles de seguridad que no son opcionales:
 *
 *  1. El correo se guarda en `localStorage` al pedir el enlace y se recupera
 *     al volver. NUNCA se lee de la query string, ni aunque estuviera ahi: la
 *     propia documentacion de Firebase lo senala como vector de inyeccion de
 *     sesion, porque permitiria enviar a alguien un enlace que le firma como
 *     otra persona.
 *
 *  2. Si el enlace se abre en otro dispositivo, el correo no esta guardado y
 *     se pide de nuevo. Es un paso mas, pero es justo lo que impide que quien
 *     intercepte el enlace lo use sin conocer la direccion.
 */

const EMAIL_STORAGE_KEY = 'qr:emailForSignIn';

/**
 * Cancelaciones de la ventana de Google que NO son errores.
 *
 * Cerrar la ventana o pulsar fuera es una decision, no un fallo, y ensenar
 * "algo ha ido mal" por haberse arrepentido es de las cosas que mas desconfian
 * de un producto.
 */
const CANCELACIONES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

interface AuthState {
  readonly user: User | null;
  readonly loading: boolean;
  readonly isSuperadmin: boolean;
}

interface AuthContextValue extends AuthState {
  readonly sendLink: (email: string) => Promise<void>;
  /** Devuelve false si la persona cerro la ventana sin elegir cuenta. */
  readonly signInWithGoogle: () => Promise<boolean>;
  readonly completeSignIn: (emailFromForm?: string) => Promise<User>;
  readonly logout: () => Promise<void>;
  readonly pendingEmail: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredEmail(): string | null {
  try {
    return globalThis.localStorage?.getItem(EMAIL_STORAGE_KEY) ?? null;
  } catch {
    // Navegacion privada o almacenamiento bloqueado: se pedira el correo.
    return null;
  }
}

function storeEmail(email: string): void {
  try {
    globalThis.localStorage?.setItem(EMAIL_STORAGE_KEY, email);
  } catch {
    // Sin almacenamiento el flujo sigue funcionando, solo pide el correo otra
    // vez al volver del enlace.
  }
}

function clearStoredEmail(): void {
  try {
    globalThis.localStorage?.removeItem(EMAIL_STORAGE_KEY);
  } catch {
    /* nada que limpiar */
  }
}

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    isSuperadmin: false,
  });

  useEffect(() => {
    // `onIdTokenChanged` y no `onAuthStateChanged`: asi el rol de superadmin,
    // que viaja como custom claim dentro del token, se refresca cuando el
    // token se renueva y no hace falta cerrar sesion para verlo aplicado.
    return onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setState({ user: null, loading: false, isSuperadmin: false });
        return;
      }
      const token = await user.getIdTokenResult();
      setState({
        user,
        loading: false,
        isSuperadmin: token.claims['superadmin'] === true,
      });
    });
  }, []);

  const sendLink = useCallback(async (email: string) => {
    const normalized = email.trim().toLowerCase();

    await sendSignInLinkToEmail(auth, normalized, {
      // El enlace vuelve siempre a esta misma aplicacion, en el origen actual:
      // asi funciona igual en local, en un canal de vista previa y en
      // produccion sin tocar configuracion.
      url: `${globalThis.location.origin}/acceso/completar`,
      handleCodeInApp: true,
    });

    storeEmail(normalized);
  }, []);

  /**
   * Entrar con Google.
   *
   * En ventana emergente y no con redireccion: la redireccion se lleva la
   * pagina entera y vuelve con el estado perdido, y en los navegadores que
   * bloquean cookies de terceros depende de que el dominio de autenticacion
   * sea el mismo que el de la aplicacion. La emergente no tiene ninguno de los
   * dos problemas. Si el navegador la bloquea —pasa, sobre todo en moviles—,
   * se cae a la redireccion, que ahi si funciona.
   */
  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    // Preguntar siempre que cuenta: quien tiene la del trabajo y la personal
    // abiertas a la vez entraria con la que Google decida, que no siempre es
    // la que queria, y ademas se crearia una cuenta nueva sin enterarse.
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      await signInWithPopup(auth, provider);
      return true;
    } catch (error) {
      const code = (error as { code?: string }).code ?? '';
      if (CANCELACIONES.has(code)) return false;
      if (code === 'auth/popup-blocked') {
        await signInWithRedirect(auth, provider);
        return true;
      }
      throw error;
    }
  }, []);

  const completeSignIn = useCallback(async (emailFromForm?: string) => {
    const href = globalThis.location.href;
    if (!isSignInWithEmailLink(auth, href)) {
      throw new Error('Este enlace de acceso no es valido.');
    }

    const email = emailFromForm?.trim().toLowerCase() ?? readStoredEmail();
    if (!email) {
      throw new Error('Necesitamos tu correo para completar el acceso.');
    }

    const credential = await signInWithEmailLink(auth, email, href);
    clearStoredEmail();
    return credential.user;
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    clearStoredEmail();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      sendLink,
      signInWithGoogle,
      completeSignIn,
      logout,
      pendingEmail: readStoredEmail(),
    }),
    [state, sendLink, signInWithGoogle, completeSignIn, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  }
  return context;
}
